/**
 * 修复 P2：统一格式化工具函数。
 * 替换项目中分散的金额、日期等重复代码，确保风格一致。
 */
import type { BillingCycle } from '@/types/plan'

/**
 * 将分转换为元并格式化为 ¥X.XX。
 */
export function formatCentsToYuan(cents: number, showFree = false): string {
  if (showFree && cents === 0) return '免费'
  return `¥${(cents / 100).toFixed(2)}`
}

/**
 * 格式化日期为中文日期字符串（仅日期，不含时间）。
 */
export function formatDate(date: Date | string | undefined | null): string {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('zh-CN')
}

/**
 * 格式化日期时间为中文日期时间字符串。
 */
export function formatDateTime(date: Date | string | undefined | null): string {
  if (!date) return '-'
  return new Date(date).toLocaleString('zh-CN')
}

/**
 * 格式化计费周期为中文标签。
 */
export function formatBillingCycle(cycle: BillingCycle | string): string {
  const map: Record<string, string> = {
    monthly: '月付',
    yearly: '年付',
    lifetime: '一次性',
  }
  return map[cycle] ?? cycle
}

/**
 * Date → 后端 @RequestParam LocalDateTime 可绑定的本地时间串（无 Z 后缀、无时区换算）。
 *
 * 为什么不用 toISOString()：
 * 1. ISO 串带 `Z` 后缀，Spring 的 LocalDateTime 默认绑定解析失败（ConversionFailedException → 400）；
 * 2. toISOString 会把时刻换算成 UTC 墙钟，而日志 createdAt 以服务器默认时区墙钟存储，
 *    浏览器与服务器时区不一致时筛选窗口会整体偏移。
 *
 * 时区口径（重要假设）：本函数发送**用户浏览器本地墙钟**，等价于假设"服务器时区 == 用户时区"
 * （本项目面向国内单时区部署，Docker 生产环境请确保容器 TZ=Asia/Shanghai）。跨时区部署时，
 * 应在后端统一改为接收 ISO 8601 带时区串并转 UTC 存储，而非在各前端调用点换算。
 *
 * 现有消费方：统一审计日志的 service 与 mock 数据源（services/auditApi、mock/audit-log）；
 * 新的日志类 API 请复用本函数，不要再各自手写 padStart 副本。
 */
export function toLocalDateTimeParam(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}` +
    `T${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`
  )
}

/**
 * 修复审查#12：Date → 本地日历日 `yyyy-MM-dd`（后端 LocalDate 入参）。
 *
 * 不用 toISOString().split('T')[0]：那串先换算成 UTC 墙钟再截断，东八区 00:00–08:00
 * 之间会被归到「前一天」，日历筛选/按日取数因此漏记录或取错天。
 * 时区假设同 toLocalDateTimeParam：发送浏览器本地墙钟。
 */
export function toLocalDateKey(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
}
/**
 * Date → 后端 LocalDate（JSON body）可解析的本地日期串 `YYYY-MM-DD`。
 *
 * 为什么不用 toISOString().slice(0, 10)：toISOString 换算成 UTC 墙钟，东八区 0-8 点的
 * 日期会回退一天（工作记录日历模块曾有 UTC 截断归错天的先例）。本函数按浏览器本地
 * 墙钟拼接，与 toLocalDateTimeParam 同一时区口径。
 *
 * 现有消费方：宣传报道 reportCreate 的 eventDate 提交（后端 CreateReportDto.eventDate
 * 为 LocalDate，ISO 时间串会被严格解析拒绝 → 400）。
 */
export function toLocalDateParam(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
}

/**
 * 本地日期串（"YYYY-MM-DD"，兼容带时间部分）→ 本地时区 Date。
 *
 * 为什么不用 new Date(str)：串形式按 UTC 解析，东八区显示无碍但西半球时区会跨日；
 * 与 toLocalDateParam 互为逆运算，同一时区口径。
 *
 * 现有消费方：宣传报道 store 恢复编辑、StepBasicInfo/StepPreviewPublish 日期选择器回写。
 */
export function parseLocalDateParam(s: string | null | undefined): Date | null {
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/**
 * 修复角色权限三审#9：ISO 时间串 → 中文日期时间（两位月/日/时/分）。
 * 此前角色列表、角色详情、权限模板三视图各拷一份，收敛为单一实现。
 */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/**
 * 统计中文字符数 + 英文单词数（报道/工作记录正文字数统计口径）。
 * 此前该逻辑在 reportCreate store 与 ReportDetailView 各写一份，收敛到单一实现。
 */
export function countMixedWords(text: string): number {
  if (!text) return 0
  const cnChars = (text.match(/[一-龥]/g) ?? []).length
  const enWords = (text.match(/[a-zA-Z]+/g) ?? []).length
  return cnChars + enWords
}

/**
 * 商户订单号（微信/支付宝的 out_trade_no）：paymentId 去掉连字符的 32 位 hex。
 *
 * 与后端 PaymentChannelHandler.toOutTradeNo 同一规则——下单、查单、回调反解全链路只用这一个口径，
 * 界面显示必须逐字一致，否则复制到微信商户平台「商户订单号」列搜不到单。
 * 不做 UUID 合法性校验：mock 渠道的 paymentId 是 pay-1 这类非 UUID 串，后端同样只做 replace。
 */
export function toMerchantOrderNo(paymentId: string | null | undefined): string {
  if (!paymentId) return ''
  return paymentId.replace(/-/g, '')
}

